(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let revealObserver;

  const announce = (message) => {
    const toast = document.querySelector('[data-wowcat-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    window.requestAnimationFrame(() => toast.classList.add('show'));
    window.clearTimeout(Number(toast.dataset.timer || 0));
    toast.dataset.timer = String(window.setTimeout(() => {
      toast.classList.remove('show');
      window.setTimeout(() => { toast.hidden = true; }, 240);
    }, 2600));
  };

  const setupReveal = (root = document) => {
    const candidates = root.querySelectorAll([
      '.home-products-head', '.promise', '.product-card', '.feature-card', '.story-split',
      '.page-hero-inner', '.link-card', '.visual-split', '.shape-chart', '.guide-block',
      '.guide-inner > *', '.reward-layout > *', '.archive-intro', '.catalog-tools',
      '.filter-row', '.product-gallery', '.product-info', '.panel', '.account-card',
      '.club-inner > *', '.footer-copy', '.footer-image', '.collection-card', '.contact-layout > *',
    ].join(','));
    candidates.forEach((element, index) => {
      if (element.dataset.revealReady) return;
      element.dataset.revealReady = 'true';
      element.classList.add('wowcat-reveal');
      element.style.setProperty('--reveal-order', String(index % 6));
      if (reduceMotion.matches || !('IntersectionObserver' in window)) element.classList.add('is-visible');
      else revealObserver?.observe(element);
    });
  };

  const closeShopMenu = (menu) => {
    const toggle = menu?.querySelector('.shop-toggle');
    menu?.classList.remove('is-open');
    toggle?.setAttribute('aria-expanded', 'false');
  };

  const setupHeader = (root = document) => {
    const header = root.querySelector('[data-wowcat-header]') || document.querySelector('[data-wowcat-header]');
    if (!header || header.dataset.interactive) return;
    header.dataset.interactive = 'true';
    const menu = header.querySelector('[data-shop-menu]');
    const toggle = menu?.querySelector('.shop-toggle');
    toggle?.addEventListener('click', () => {
      const open = !menu.classList.contains('is-open');
      menu.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
    });
    toggle?.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowDown') return;
      event.preventDefault();
      menu.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => menu.querySelector('.shop-dropdown a')?.focus()));
    });
    document.addEventListener('pointerdown', (event) => {
      if (menu && !menu.contains(event.target)) closeShopMenu(menu);
    });
    menu?.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      closeShopMenu(menu);
      toggle?.focus();
    });

    const button = document.querySelector('.menu-button');
    const panel = document.querySelector('.mobile-panel');
    const overlay = document.querySelector('.mobile-menu-overlay');
    let returnFocus = null;
    let openFrame = 0;
    if (!button || !panel || button.dataset.interactive) return;
    button.dataset.interactive = 'true';
    const closeMobile = (restoreFocus = true) => {
      if (openFrame) window.cancelAnimationFrame(openFrame);
      openFrame = 0;
      document.body.classList.remove('menu-open');
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-label', 'Open menu');
      panel.setAttribute('aria-hidden', 'true');
      window.setTimeout(() => {
        if (document.body.classList.contains('menu-open')) return;
        panel.hidden = true;
        if (overlay) overlay.hidden = true;
      }, reduceMotion.matches ? 0 : 300);
      if (restoreFocus) (returnFocus || button).focus();
    };
    const openMobile = () => {
      returnFocus = document.activeElement;
      panel.hidden = false;
      if (overlay) overlay.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      button.setAttribute('aria-expanded', 'true');
      button.setAttribute('aria-label', 'Close menu');
      openFrame = window.requestAnimationFrame(() => {
        openFrame = 0;
        if (button.getAttribute('aria-expanded') !== 'true') return;
        document.body.classList.add('menu-open');
        panel.querySelector('a')?.focus({ preventScroll: true });
      });
    };
    button.addEventListener('click', () => button.getAttribute('aria-expanded') === 'true' ? closeMobile() : openMobile());
    overlay?.addEventListener('click', () => closeMobile());
    panel.addEventListener('click', (event) => { if (event.target.closest('a')) closeMobile(false); });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && button.getAttribute('aria-expanded') === 'true') closeMobile();
      if (event.key !== 'Tab' || !document.body.classList.contains('menu-open')) return;
      const focusable = [...panel.querySelectorAll('a[href], button:not([disabled])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
  };

  const setupQuantity = (root = document) => {
    root.querySelectorAll('[data-quantity]').forEach((control) => {
      if (control.dataset.interactive) return;
      control.dataset.interactive = 'true';
      const input = control.querySelector('input[type="number"]');
      const change = (step) => {
        if (!input) return;
        const min = Number(input.min || 1);
        const max = Number(input.max || 99);
        input.value = String(Math.min(max, Math.max(min, Number(input.value || min) + step)));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      control.querySelector('[data-quantity-minus]')?.addEventListener('click', () => change(-1));
      control.querySelector('[data-quantity-plus]')?.addEventListener('click', () => change(1));
    });
  };

  const setupProduct = (root = document) => {
    root.querySelectorAll('[data-variant-select]').forEach((select) => {
      if (select.dataset.interactive) return;
      select.dataset.interactive = 'true';
      const form = select.closest('form');
      const price = document.querySelector('[data-product-price]');
      const submit = form?.querySelector('[name="add"]');
      const sync = () => {
        const option = select.selectedOptions[0];
        if (price && option?.dataset.price) price.textContent = option.dataset.price;
        if (submit) {
          const available = option?.dataset.available !== 'false' && !option?.disabled;
          submit.disabled = !available;
          submit.textContent = available ? 'Add to bag' : 'Sold out';
        }
      };
      select.addEventListener('change', sync);
      sync();
    });
    const mainImage = root.querySelector('[data-product-main-image]') || document.querySelector('[data-product-main-image]');
    root.querySelectorAll('[data-product-thumbnail]').forEach((thumbnail) => {
      if (thumbnail.dataset.interactive) return;
      thumbnail.dataset.interactive = 'true';
      thumbnail.addEventListener('click', () => {
        if (!mainImage) return;
        mainImage.removeAttribute('srcset');
        mainImage.src = thumbnail.dataset.productThumbnail;
        mainImage.alt = thumbnail.dataset.productAlt || mainImage.alt;
        root.querySelectorAll('[data-product-thumbnail]').forEach((item) => item.setAttribute('aria-pressed', 'false'));
        thumbnail.setAttribute('aria-pressed', 'true');
      });
    });
    root.querySelectorAll('[data-product-form]').forEach((form) => {
      if (form.dataset.ajaxReady) return;
      form.dataset.ajaxReady = 'true';
      form.addEventListener('submit', async (event) => {
        if (!window.fetch || window.Shopify?.designMode) return;
        event.preventDefault();
        const submit = form.querySelector('[name="add"]');
        const defaultLabel = submit?.textContent.trim() || 'Add to bag';
        if (submit) { submit.disabled = true; submit.textContent = 'Adding…'; submit.setAttribute('aria-busy', 'true'); }
        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || 'This set could not be added.');
          const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
          const cart = await cartResponse.json();
          document.querySelectorAll('[data-cart-count]').forEach((count) => { count.textContent = String(cart.item_count || 0); });
          announce(`${result.product_title || 'Nail set'} added to your bag.`);
          if (submit) submit.textContent = 'Added ✓';
          window.setTimeout(() => { if (submit) submit.textContent = defaultLabel; }, 1800);
        } catch (error) {
          announce(error.message || 'This set could not be added.');
          if (submit) submit.textContent = 'Try again';
        } finally {
          if (submit) { submit.disabled = false; submit.removeAttribute('aria-busy'); }
        }
      });
    });
  };

  const setupCart = (root = document) => {
    root.querySelectorAll('[data-cart-quantity]').forEach((control) => {
      if (control.dataset.interactive) return;
      control.dataset.interactive = 'true';
      const input = control.querySelector('input');
      const update = (step) => {
        if (!input) return;
        input.value = String(Math.max(0, Number(input.value || 0) + step));
        control.closest('form')?.requestSubmit(control.closest('form')?.querySelector('[name="update"]'));
      };
      control.querySelector('[data-cart-minus]')?.addEventListener('click', () => update(-1));
      control.querySelector('[data-cart-plus]')?.addEventListener('click', () => update(1));
    });
  };

  const setupAddresses = (root = document) => {
    root.querySelectorAll('[data-address-edit]').forEach((button) => {
      if (button.dataset.interactive) return;
      button.dataset.interactive = 'true';
      button.addEventListener('click', () => {
        const panel = document.getElementById(button.dataset.addressEdit);
        if (!panel) return;
        panel.hidden = !panel.hidden;
        button.textContent = panel.hidden ? 'Edit' : 'Cancel';
        if (!panel.hidden) panel.querySelector('input,select')?.focus();
      });
    });
    root.querySelectorAll('[data-confirm]').forEach((button) => {
      if (button.dataset.interactive) return;
      button.dataset.interactive = 'true';
      button.addEventListener('click', (event) => {
        if (!window.confirm(button.dataset.confirm)) event.preventDefault();
      });
    });
  };

  const ready = (root = document) => {
    document.documentElement.classList.remove('no-js');
    document.documentElement.classList.add('js');
    setupHeader(root);
    setupQuantity(root);
    setupProduct(root);
    setupCart(root);
    setupAddresses(root);
    root.querySelectorAll('[data-auto-submit]').forEach((select) => {
      if (select.dataset.interactive) return;
      select.dataset.interactive = 'true';
      select.addEventListener('change', () => select.form?.requestSubmit());
    });
    setupReveal(root);
  };

  if (!reduceMotion.matches && 'IntersectionObserver' in window) {
    document.documentElement.classList.add('motion-ready');
    revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => ready());
  else ready();
  document.addEventListener('shopify:section:load', (event) => ready(event.target));
})();
